import { defineAttribute, defineCSSState, defineProperty, defineRef, GemElement, nativeDefineElement } from './element';
import { camelToKebabCase } from './utils';
function pushStaticField(target, field, member, isSet = false) {
    const cls = target.constructor;
    const current = cls[field];
    if (!cls.hasOwnProperty(field)) {
        Object.defineProperty(cls, field, {
            value: isSet ? new Set(current) : current ? Array.from(current) : [],
        });
    }
    cls[field][isSet ? 'add' : 'push'](member);
}
/**
 * 引用元素，只有第一个标记 ref 的元素有效
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @refobject childRef: RefObject<Children>;
 *    loadHandle = () => {
 *      const { element } = this.childRef;
 *      console.log(element);
 *    };
 *    render() {
 *      render html`<div ref=${this.childRef.ref}><div>`;
 *    }
 *  }
 * ```
 */
export function refobject(target, prop) {
    const attr = camelToKebabCase(prop);
    pushStaticField(target, 'defineRefs', attr);
    defineRef(target, prop, attr);
}
/**
 * 定义一个响应式的 attribute，驼峰字段名将自动映射到烤串 attribute，默认值为空字符串
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @attribute attr: string;
 *  }
 * ```
 */
function defineAttr(target, prop, attr) {
    pushStaticField(target, 'observedAttributes', attr);
    defineAttribute(target, prop, attr);
}
export function attribute(target, prop) {
    defineAttr(target, prop, camelToKebabCase(prop));
}
export function boolattribute(target, prop) {
    const attr = camelToKebabCase(prop);
    pushStaticField(target, 'booleanAttributes', attr, true);
    defineAttr(target, prop, attr);
}
export function numattribute(target, prop) {
    const attr = camelToKebabCase(prop);
    pushStaticField(target, 'numberAttributes', attr, true);
    defineAttr(target, prop, attr);
}
/**
 * 定义一个响应式的 property，注意值可能为 `undefined`
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @property prop: Data | undefined;
 *  }
 * ```
 */
export function property(target, prop) {
    pushStaticField(target, 'observedPropertys', prop);
    defineProperty(target, prop);
}
/**
 * 定义一个元素[内部](https://html.spec.whatwg.org/multipage/custom-elements.html#elementinternals) state，
 * 类似 `:checked`，用于自定义 css 伪类（:--xxx），默认值即为字段名。
 * 重新赋值转换 css 伪类
 * 将来也可能用于 IDE 识别
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @state state: boolean;
 *  }
 * ```
 */
export function state(target, prop) {
    const attr = camelToKebabCase(prop);
    pushStaticField(target, 'defineCSSStates', attr);
    defineCSSState(target, prop, `--${attr}`);
}
/**
 * 定义一个内部 slot，默认值即为字段名，不能使用全局属性 `slot`
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @slot slot: string;
 *    @slot static slot: string;
 *  }
 * ```
 */
export function slot(target, prop) {
    const attr = camelToKebabCase(target[prop] || prop);
    target[prop] = attr;
    pushStaticField(target instanceof GemElement ? target : target.prototype, 'defineSlots', attr);
}
/**
 * 定义一个内部 part，默认值即为字段名，不能使用全局属性 `part`
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @part part: string;
 *    @part static part: string;
 *  }
 * ```
 */
export function part(target, prop) {
    const attr = camelToKebabCase(target[prop] || prop);
    target[prop] = attr;
    pushStaticField(target instanceof GemElement ? target : target.prototype, 'defineParts', attr);
}
/**
 * 定义一个事件发射器，类似 `HTMLElement.click`，
 * 调用时将同步发送一个和字段名称的烤串式格式的自定义事件
 *
 * For example
 * ```ts
 *  class App extends GemElement {
 *    @emitter load: Function;
 *  }
 * ```
 */
export function emitter(target, prop) {
    defineEmitter(target, prop);
}
export function globalemitter(target, prop) {
    defineEmitter(target, prop, { bubbles: true, composed: true });
}
function defineEmitter(target, prop, options) {
    const event = camelToKebabCase(prop);
    pushStaticField(target, 'defineEvents', event);
    defineProperty(target, prop, event, options);
}
/**
 * 分配一个构造的样式表
 *
 * For example
 * ```ts
 *  @adoptedStyle(stylesheet)
 *  class App extends GemElement {}
 * ```
 */
export function adoptedStyle(style) {
    return function (cls) {
        const con = cls;
        pushStaticField(con.prototype, 'adoptedStyleSheets', style);
    };
}
/**
 * 链接一个 store，当 store 更新时更新元素
 *
 * For example
 * ```ts
 *  @connectStore(store)
 *  class App extends GemElement {}
 * ```
 */
export function connectStore(store) {
    // 这里的签名该怎么写？
    return function (cls) {
        const con = cls;
        pushStaticField(con.prototype, 'observedStores', store);
    };
}
/**
 * 定义自定义元素
 *
 * For example
 * ```ts
 *  @customElement('my-element')
 *  class App extends GemElement {}
 * ```
 */
export function customElement(name) {
    return function (cls) {
        nativeDefineElement(name, cls);
    };
}
//# sourceMappingURL=decorators.js.map